"use client";

import { Span } from "@chakra-ui/react";
import { ThemeProvider, useTheme } from "next-themes";

import { forwardRef } from "react";

/**
 * @param {import('next-themes').ThemeProviderProps} props
 */
export function ColorModeProvider(props) {
  return (
    <ThemeProvider attribute="class" disableTransitionOnChange {...props} />
  );
}

export function useColorMode() {
  const { resolvedTheme, setTheme } = useTheme();

  const toggleColorMode = () => {
    setTheme(resolvedTheme === "light" ? "dark" : "light");
  };

  return {
    colorMode: resolvedTheme,
    setColorMode: setTheme,
    toggleColorMode,
  };
}

/**
 * @template T1
 * @template T2
 * @param {T1} light
 * @param {T2} dark
 * @returns {T1 | T2}
 */
export function useColorModeValue(light, dark) {
  const { colorMode } = useColorMode();

  return colorMode === "light" ? light : dark;
}

export const LightMode = forwardRef(
  /**
   * @typedef {object} LightModeProps
   * @prop {React.ReactNode} children
   */
  /**
   * @param {LightModeProps} props
   */
  function LightMode({ children }, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme light"
        colorPalette="gray"
        colorScheme="light"
        ref={ref}
        children={children}
      />
    );
  },
);

export const DarkMode = forwardRef(
  /**
   * @typedef {object} DarkModeProps
   * @prop {React.ReactNode} children
   */
  /**
   * @param {DarkModeProps} props
   */
  function DarkMode({ children }, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme dark"
        colorPalette="gray"
        colorScheme="dark"
        ref={ref}
        children={children}
      />
    );
  },
);
